import {useRef} from "react";
import {ControlPosition, useControl} from 'react-map-gl/mapbox';
import MapboxDraw, {
  DrawCreateEvent,
  DrawDeleteEvent,
  DrawModeChangeEvent,
  DrawUpdateEvent
} from '@mapbox/mapbox-gl-draw';

// Types ----

export type DrawControlProps = ConstructorParameters<typeof MapboxDraw>[0] & {
  position?: ControlPosition;

  onLoad?: (event: object, control: MapboxDraw) => void;
  onModeChange?: (event: DrawModeChangeEvent, control: MapboxDraw) => void;
  onCreate?: (event: DrawCreateEvent, control: MapboxDraw) => void;
  onUpdate?: (event: DrawUpdateEvent, control: MapboxDraw) => void;
  onDelete?: (event: DrawDeleteEvent, control: MapboxDraw) => void;
};

// Component ----

function DrawControl(props: DrawControlProps) {
  const drawRef = useRef<MapboxDraw | null>(null);
  const propsRef = useRef(props);

  // Using a ref to prevent stale values
  propsRef.current = props;

  function handleLoad(event: object) {
    if (props.onLoad && drawRef.current) props.onLoad(event, drawRef.current)
  }

  function handleDrawModeChange(event: DrawModeChangeEvent): void {
    const {onModeChange} = propsRef.current;
    if (onModeChange && drawRef.current) onModeChange(event, drawRef.current)
  }

  function handleDrawCreate(event: DrawCreateEvent): void {
    const {onCreate} = propsRef.current;
    if (onCreate && drawRef.current) onCreate(event, drawRef.current)
  }

  function handleDrawUpdate(event: DrawUpdateEvent): void {
    const {onUpdate} = propsRef.current;
    if (onUpdate && drawRef.current) onUpdate(event, drawRef.current)
  }

  function handleDrawDelete(event: DrawDeleteEvent): void {
    const {onDelete} = propsRef.current;
    if (onDelete && drawRef.current) onDelete(event, drawRef.current)
  }

  useControl<MapboxDraw>(
    // On Create
    () => {
      const drawInstance = new MapboxDraw(props);
      drawRef.current = drawInstance;

      console.debug("[Map:DrawControl] Created");

      return drawInstance;
    },

    // On Add
    ({map}) => {
      map.on('load', handleLoad)
      map.on('draw.modechange', handleDrawModeChange);
      map.on('draw.create', handleDrawCreate);
      map.on('draw.update', handleDrawUpdate);
      map.on('draw.delete', handleDrawDelete);

      console.debug("[Map:DrawControl] Added");
    },

    // On Remove
    ({map}) => {
      map.off('load', handleLoad)
      map.off('draw.modechange', handleDrawModeChange);
      map.off('draw.create', handleDrawCreate);
      map.off('draw.update', handleDrawUpdate);
      map.off('draw.delete', handleDrawDelete);

      console.debug("[Map:DrawControl] Removed");
    },

    // Control Options
    {
      position: props.position
    }
  );

  return null;
}

// Export ----

export default DrawControl;